import { useMemo } from 'react'
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'

import { queryKeys, QUERY_CONFIG } from '../../lib/react-query'
import { complianceApi } from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import { useFeature } from '../../Utils/featureFlagEngine'
import {
  buildFindingsParams,
  MAX_PAGE_SIZE,
} from '../../app/pages/compliance/lib/findingParams'
import type { ComplianceExceptionInput } from '../../app/modules/types/compliance'

/**
 * Compliance data access.
 *
 * Every query is gated on BOTH the organization and the `compliance` feature:
 * `FeatureGuard` only hides UI, so a hook left ungated would keep firing
 * requests for a feature the user cannot see.
 */
function useComplianceGate() {
  const orgId = useAuthStore((s) => s.orgId)
  const { isEnabled } = useFeature()
  return { orgId, enabled: !!orgId && isEnabled('compliance') }
}

/**
 * The findings list.
 *
 * The URL's search params are the source of truth, but they are translated
 * through `buildFindingsParams` rather than forwarded: the page URL also
 * carries `organization` (read by nothing) and may carry an `ordering` left by
 * another list page, and sorting is not a query param on this endpoint yet.
 */
export function useComplianceFindings() {
  const { orgId, enabled } = useComplianceGate()
  const [searchParams] = useSearchParams()

  const params = useMemo(() => {
    const filters: Record<string, string[]> = {}
    searchParams.forEach((value, key) => {
      filters[key] = value.split(',').filter(Boolean)
    })
    const page = Number(searchParams.get('page') ?? '1')
    return buildFindingsParams({
      filters,
      page: Number.isFinite(page) && page > 0 ? page : 1,
      pageSize: MAX_PAGE_SIZE,
    })
  }, [searchParams])

  return useQuery({
    queryKey: queryKeys.compliance.list(orgId, params),
    queryFn: () => complianceApi.getFindings(params),
    enabled,
  })
}

/**
 * One asset's findings, driven by explicit state rather than the URL — the
 * asset detail page's URL belongs to the page (`id`, `tab`), and a filter write
 * through `useUrlState` would rebuild it and drop both.
 */
export function useAssetComplianceFindings(
  assetId: string,
  filters: Record<string, string[] | undefined>,
  page: number,
) {
  const { orgId, enabled } = useComplianceGate()
  const params = useMemo(
    () =>
      buildFindingsParams({
        filters: { ...filters, asset: [assetId] },
        page,
        pageSize: MAX_PAGE_SIZE,
      }),
    [assetId, filters, page],
  )
  return useQuery({
    queryKey: queryKeys.compliance.list(orgId, params),
    queryFn: () => complianceApi.getFindings(params),
    enabled: enabled && !!assetId,
  })
}

export function useComplianceFilterOptions(asset?: string) {
  const { orgId, enabled } = useComplianceGate()
  return useQuery({
    queryKey: queryKeys.compliance.filterOptions(orgId, asset),
    queryFn: () => complianceApi.getFilterOptions(asset),
    staleTime: QUERY_CONFIG.LISTS.staleTime,
    enabled,
  })
}

export function useComplianceFinding(findingId: string | null) {
  const { orgId, enabled } = useComplianceGate()
  return useQuery({
    queryKey: queryKeys.compliance.detail(orgId, findingId || ''),
    queryFn: () => complianceApi.getFinding(findingId!),
    enabled: enabled && !!findingId,
    staleTime: QUERY_CONFIG.LISTS.staleTime,
  })
}

/** Organization posture now. */
export function useComplianceStats() {
  const { orgId, enabled } = useComplianceGate()
  return useQuery({
    queryKey: queryKeys.compliance.stats(orgId),
    queryFn: () => complianceApi.getStats(),
    staleTime: QUERY_CONFIG.LISTS.staleTime,
    enabled,
  })
}

/** Posture per (benchmark, profile). */
export function useComplianceBenchmarks() {
  const { orgId, enabled } = useComplianceGate()
  return useQuery({
    queryKey: queryKeys.compliance.benchmarks(orgId),
    queryFn: () => complianceApi.getBenchmarks(),
    staleTime: QUERY_CONFIG.LISTS.staleTime,
    enabled,
  })
}

/**
 * What one scan scored. Only fetched while the list is actually scoped to a
 * scan — a `scan_id` belonging to another organization is a 404, so this must
 * not fire speculatively.
 */
export function useComplianceScanStats(scanId: string | null) {
  const { orgId, enabled } = useComplianceGate()
  return useQuery({
    queryKey: queryKeys.compliance.scanStats(orgId, scanId || ''),
    queryFn: () => complianceApi.getScanStats(scanId!),
    enabled: enabled && !!scanId,
    staleTime: QUERY_CONFIG.LISTS.staleTime,
  })
}

/** The evidence trail: one row per scan that evaluated this control. */
export function useComplianceOccurrences(findingId: string | null) {
  const { orgId, enabled } = useComplianceGate()

  const query = useInfiniteQuery({
    queryKey: queryKeys.compliance.occurrences(orgId, findingId || ''),
    queryFn: ({ pageParam }) =>
      complianceApi.getOccurrences(findingId!, `page=${pageParam}&page_size=${MAX_PAGE_SIZE}`),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.current_page < lastPage.total_pages ? lastPage.current_page + 1 : undefined,
    enabled: enabled && !!findingId,
  })

  const occurrences = useMemo(
    () => (query.data?.pages ?? []).flatMap((page) => page.results),
    [query.data],
  )

  return {
    occurrences,
    totalItems: query.data?.pages?.[0]?.total_items ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    hasMore: !!query.hasNextPage,
    isFetchingMore: query.isFetchingNextPage,
    loadMore: () => query.fetchNextPage(),
  }
}

/** Verdict changes, exceptions and comments, as one history. */
export function useComplianceTimeline(findingId: string | null) {
  const { orgId, enabled } = useComplianceGate()

  const query = useInfiniteQuery({
    queryKey: queryKeys.compliance.timeline(orgId, findingId || ''),
    queryFn: ({ pageParam }) =>
      complianceApi.getTimeline(findingId!, `page=${pageParam}&page_size=${MAX_PAGE_SIZE}`),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.current_page < lastPage.total_pages ? lastPage.current_page + 1 : undefined,
    enabled: enabled && !!findingId,
  })

  const events = useMemo(
    () => (query.data?.pages ?? []).flatMap((page) => page.results),
    [query.data],
  )

  return {
    events,
    isLoading: query.isLoading,
    isError: query.isError,
    hasMore: !!query.hasNextPage,
    isFetchingMore: query.isFetchingNextPage,
    loadMore: () => query.fetchNextPage(),
  }
}

export function useComplianceComments(findingId: string | null) {
  const { orgId, enabled } = useComplianceGate()

  const query = useInfiniteQuery({
    queryKey: queryKeys.compliance.comments(orgId, findingId || ''),
    queryFn: ({ pageParam }) =>
      complianceApi.getComments(findingId!, `page=${pageParam}&page_size=${MAX_PAGE_SIZE}`),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.current_page < lastPage.total_pages ? lastPage.current_page + 1 : undefined,
    enabled: enabled && !!findingId,
  })

  const comments = useMemo(
    () => (query.data?.pages ?? []).flatMap((page) => page.results),
    [query.data],
  )

  return {
    comments,
    totalItems: query.data?.pages?.[0]?.total_items ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    hasMore: !!query.hasNextPage,
    isFetchingMore: query.isFetchingNextPage,
    loadMore: () => query.fetchNextPage(),
  }
}

/**
 * A comment write also lands on the timeline (`comment_added`), so both are
 * invalidated together.
 */
function useInvalidateThread(findingId: string) {
  const orgId = useAuthStore((s) => s.orgId)
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: queryKeys.compliance.comments(orgId, findingId) })
    qc.invalidateQueries({ queryKey: queryKeys.compliance.timeline(orgId, findingId) })
  }
}

export function useAddComplianceComment(findingId: string) {
  const invalidate = useInvalidateThread(findingId)
  return useMutation({
    mutationFn: (body: string) => complianceApi.addComment(findingId, body),
    // The success toast is fired by the component, which has the intl context;
    // a hook must not own a user-visible string in a bilingual app.
    onSuccess: invalidate,
  })
}

export function useEditComplianceComment(findingId: string) {
  const invalidate = useInvalidateThread(findingId)
  return useMutation({
    mutationFn: ({ commentId, body }: { commentId: string; body: string }) =>
      complianceApi.editComment(findingId, commentId, body),
    onSuccess: invalidate,
  })
}

export function useDeleteComplianceComment(findingId: string) {
  const invalidate = useInvalidateThread(findingId)
  return useMutation({
    mutationFn: (commentId: string) => complianceApi.deleteComment(findingId, commentId),
    onSuccess: invalidate,
  })
}

/**
 * Setting or clearing an exception moves `adjusted_score` — on the finding, on
 * the organization headline and on that benchmark's row — so all three posture
 * surfaces are invalidated alongside the finding and its timeline.
 *
 * The finding's `status` is NOT affected; the backend returns it unchanged.
 */
function useInvalidateException(findingId: string) {
  const orgId = useAuthStore((s) => s.orgId)
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: queryKeys.compliance.detail(orgId, findingId) })
    qc.invalidateQueries({ queryKey: queryKeys.compliance.timeline(orgId, findingId) })
    qc.invalidateQueries({ queryKey: queryKeys.compliance.stats(orgId) })
    qc.invalidateQueries({ queryKey: queryKeys.compliance.benchmarks(orgId) })
    qc.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === orgId &&
        query.queryKey[1] === 'compliance' &&
        query.queryKey[2] === 'list',
    })
    // The asset detail pages carry the same `adjusted_score` in their insights
    // payload's `compliance` block.
    qc.invalidateQueries({ queryKey: queryKeys.assetInsights.all(orgId) })
  }
}

export function useSetComplianceException(findingId: string) {
  const invalidate = useInvalidateException(findingId)
  return useMutation({
    mutationFn: (payload: ComplianceExceptionInput) =>
      complianceApi.setException(findingId, payload),
    onSuccess: invalidate,
  })
}

export function useClearComplianceException(findingId: string) {
  const invalidate = useInvalidateException(findingId)
  return useMutation({
    mutationFn: () => complianceApi.clearException(findingId),
    onSuccess: invalidate,
  })
}
